import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDropdownValues,
  getSchemaByTable,
  getColumnsByTable,
  getLabelNames,
  getListPrivileges,
  populateNavigationStub,
  countUnreadDirectMessageConversation,
  countUnreadGroupChatConversation,
} from '../scripts/helpers';

// keys are exported so callers can invalidate after mutations
export const queryKeys = {
  dropdown: (tableName, columnName) => ['dropdown', tableName, columnName],
  schema: (tableSlug) => ['schema', tableSlug],
  columns: (tableSlug) => ['columns', tableSlug],
  labels: (tableSlug) => ['labels', tableSlug],
  listPrivileges: (token) => ['listPrivileges', token],
  navigationStub: (workerId, jobId, problemId) => ['navigationStub', workerId, jobId, problemId],
  unreadDirect: (userId) => ['unreadDirect', userId],
  unreadGroup: (userId) => ['unreadGroup', userId],
};

const unwrap = (res) => (res && 'data' in res ? res.data : res);

export function useDropdownValues(tableName, columnName, options = {}) {
  return useQuery({
    queryKey: queryKeys.dropdown(tableName, columnName),
    queryFn: () => getDropdownValues(tableName, columnName).then(unwrap),
    enabled: Boolean(tableName && columnName),
    staleTime: 30 * 60 * 1000, // reference data — long stale window
    ...options,
  });
}

export function useSchema(tableSlug, options = {}) {
  return useQuery({
    queryKey: queryKeys.schema(tableSlug),
    queryFn: () => getSchemaByTable(tableSlug).then(unwrap),
    enabled: Boolean(tableSlug),
    staleTime: Infinity, // schema is immutable per session
    cacheTime: Infinity,
    ...options,
  });
}

export function useColumns(tableSlug, options = {}) {
  return useQuery({
    queryKey: queryKeys.columns(tableSlug),
    queryFn: () => getColumnsByTable(tableSlug).then(unwrap),
    enabled: Boolean(tableSlug),
    staleTime: Infinity,
    cacheTime: Infinity,
    ...options,
  });
}

export function useLabelNames(tableSlug, options = {}) {
  return useQuery({
    queryKey: queryKeys.labels(tableSlug),
    queryFn: () => getLabelNames(tableSlug).then(unwrap),
    enabled: Boolean(tableSlug),
    staleTime: Infinity,
    cacheTime: Infinity,
    ...options,
  });
}

export function useListPrivileges(token, options = {}) {
  return useQuery({
    queryKey: queryKeys.listPrivileges(token),
    queryFn: () => getListPrivileges(token).then(unwrap),
    enabled: Boolean(token),
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

export function useNavigationStub(workerId, jobId, problemId, options = {}) {
  return useQuery({
    queryKey: queryKeys.navigationStub(workerId, jobId, problemId),
    queryFn: () => populateNavigationStub(workerId, jobId, problemId).then(unwrap),
    enabled: Boolean(workerId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useUnreadCounts(userId, options = {}) {
  const direct = useQuery({
    queryKey: queryKeys.unreadDirect(userId),
    queryFn: () => countUnreadDirectMessageConversation(userId).then(unwrap),
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
    ...options,
  });
  const group = useQuery({
    queryKey: queryKeys.unreadGroup(userId),
    queryFn: () => countUnreadGroupChatConversation(userId).then(unwrap),
    enabled: Boolean(userId),
    staleTime: 60 * 1000,
    ...options,
  });
  return { direct, group };
}

// helper for socket / mutation handlers
export function useInvalidateUnreadCounts() {
  const qc = useQueryClient();
  return (userId) => {
    qc.invalidateQueries({ queryKey: queryKeys.unreadDirect(userId) });
    qc.invalidateQueries({ queryKey: queryKeys.unreadGroup(userId) });
  };
}
